import { useRef, useState } from "react";

import { Button } from "@blueprintjs/core";
import { useMediaQuery } from "react-responsive";

import { isXsQuery } from "@/components/layout/Media";
import { SimpleTooltip } from "@/components/ui/tooltip";
import { formatDate } from "@/utils/datetime";

import { EmptyTimeslots } from "./EmptyTimeslots";
import { StackedTimeslotLoadingState } from "./StackedTimeslotLoadingState";

interface TimeslotSelectorProps {
  requiresBookingApproval: boolean;
  loading: boolean;
  timeslots: string[];
  unavailableTimeslots: string[];
  selectedTimeslots?: string[];
  onChange: (timeslots: string[]) => void;
  preferredTimeslots?: string[];
  onPreferredChange?: (preferredTimeslots: string[]) => void;
  timezone: string;
  isViewOnly: boolean;
}

export function TimeslotSelector({
  requiresBookingApproval,
  loading,
  timeslots,
  unavailableTimeslots,
  selectedTimeslots = [],
  onChange,
  preferredTimeslots = [],
  onPreferredChange,
  timezone,
  isViewOnly,
}: TimeslotSelectorProps) {
  const [showingAll, setShowingAll] = useState(false);
  const timeslotsRef = useRef<HTMLDivElement>(null);
  const isXs = useMediaQuery(isXsQuery);

  const toggleSelectTimeslot = (timeslot: string) => {
    if (requiresBookingApproval) {
      // Multiple selection mode
      const currentSelected = selectedTimeslots || [];
      let newSelected: string[];

      if (currentSelected.includes(timeslot)) {
        newSelected = currentSelected.filter((t) => t !== timeslot);
      } else {
        newSelected = [...currentSelected, timeslot];
      }

      onChange(newSelected);
    } else {
      // Single selection mode (original behavior)
      if (selectedTimeslots.includes(timeslot)) {
        onChange([]);
      } else {
        onChange([timeslot]);
      }
    }
  };

  const togglePreferredTimeslot = (timeslot: string) => {
    if (!onPreferredChange || isViewOnly) return;

    const currentPreferred = preferredTimeslots || [];
    let newPreferred: string[];

    if (currentPreferred.includes(timeslot)) {
      newPreferred = currentPreferred.filter((t) => t !== timeslot);
    } else {
      newPreferred = [...currentPreferred, timeslot];
    }

    onPreferredChange(newPreferred);
  };

  const handleSeeLess = () => {
    setShowingAll(false);
    setTimeout(() => {
      if (timeslotsRef.current) {
        const element = timeslotsRef.current;
        const elementPosition =
          element.getBoundingClientRect().top + window.pageYOffset;
        const offsetPosition = elementPosition - 220;

        window.scrollTo({
          top: offsetPosition,
          behavior: "smooth",
        });
      }
    }, 100); // Small delay to let the state update first
  };

  if (loading) {
    return <StackedTimeslotLoadingState />;
  }

  if (timeslots.length === 0 && unavailableTimeslots.length === 0) {
    return <EmptyTimeslots />;
  }

  const timeslotLimit = isXs ? 8 : 16;

  // Merge available and unavailable timeslots, sort chronologically, then slice
  const allTimeslots = [...timeslots, ...unavailableTimeslots]
    .filter((timeslot, index, array) => array.indexOf(timeslot) === index) // Remove duplicates
    .sort((a, b) => new Date(a).getTime() - new Date(b).getTime()); // Sort chronologically

  const slotsTruncated = allTimeslots.length > timeslotLimit && !showingAll;
  const numDisplayedSlots = showingAll ? undefined : timeslotLimit;
  const displayedTimeslots = allTimeslots.slice(0, numDisplayedSlots);

  return (
    <div className="my-6" ref={timeslotsRef}>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 text-center font-medium mb-2">
        {displayedTimeslots.map((timeslot) => {
          const selected = selectedTimeslots.includes(timeslot);
          const available =
            !!unavailableTimeslots.length &&
            !unavailableTimeslots.includes(timeslot);

          const baseClasses =
            "p-4 rounded-lg border-2 transition-colors flex justify-center items-center w-full";

          const cursorClass = isViewOnly ? "cursor-default" : "cursor-pointer";

          const conditionalClasses = selected
            ? "border-blue-500 bg-blue-50 text-blue-600"
            : "border-gray-300 bg-white text-gray-700 hover:border-gray-400";

          const disabledClasses = isViewOnly
            ? "border-gray-200 text-gray-500 bg-gray-50 cursor-default hover:border-gray-200 hover:text-gray-500 hover:bg-gray-50"
            : "";

          return (
            <div
              key={timeslot}
              onClick={() => !isViewOnly && toggleSelectTimeslot(timeslot)}
              className={`${baseClasses} ${cursorClass} ${conditionalClasses} ${disabledClasses}`}
            >
              <div className="flex h-2 w-2 mr-1">
                {available && (
                  <svg height="8" width="8">
                    <circle r="4" cx="4" cy="4" fill="#27AE60" />
                  </svg>
                )}
              </div>
              {formatDate(timeslot, "h:mm a", timezone)}
            </div>
          );
        })}
      </div>

      {slotsTruncated && (
        <div className="flex justify-center">
          <Button
            onClick={() => setShowingAll(true)}
            small={true}
            className="py-2 w-24"
          >
            See More
          </Button>
        </div>
      )}

      {showingAll && (
        <div className="flex justify-center">
          <Button onClick={handleSeeLess} small={true} className="py-2 w-24">
            See Less
          </Button>
        </div>
      )}

      {/* Selected times section with preferred stars */}
      {requiresBookingApproval && selectedTimeslots.length > 0 && (
        <div className="mt-8 p-4 bg-gray-50 rounded-lg border border-gray-200">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">
            Selected times
          </h3>
          <div className="space-y-2">
            {selectedTimeslots.map((timeslot) => {
              const isPreferred = preferredTimeslots.includes(timeslot);
              const formattedTime = formatDate(timeslot, "h:mm a z", timezone);
              const formattedDate = formatDate(
                timeslot,
                "EEEE, MMM do",
                timezone,
              );

              return (
                <div
                  key={timeslot}
                  className="flex items-center justify-between py-2"
                >
                  <div className="text-sm">
                    <div className="font-medium">
                      {formattedDate} at {formattedTime}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <SimpleTooltip content="Set as preferred time">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          togglePreferredTimeslot(timeslot);
                        }}
                        disabled={isViewOnly}
                        className={`w-6 h-6 flex items-center justify-center transition-colors ${
                          isViewOnly
                            ? "cursor-default"
                            : "cursor-pointer hover:bg-yellow-100"
                        } rounded`}
                        title={
                          isPreferred
                            ? "Remove from preferred"
                            : "Add to preferred"
                        }
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill={isPreferred ? "#F59E0B" : "none"}
                          stroke={isPreferred ? "#F59E0B" : "#D1D5DB"}
                          strokeWidth="2"
                          className="transition-colors"
                        >
                          <polygon points="12,2 15.09,8.26 22,9.27 17,14.14 18.18,21.02 12,17.77 5.82,21.02 7,14.14 2,9.27 8.91,8.26" />
                        </svg>
                      </button>
                    </SimpleTooltip>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleSelectTimeslot(timeslot);
                      }}
                      disabled={isViewOnly}
                      className={`w-6 h-6 flex items-center justify-center transition-colors ${
                        isViewOnly
                          ? "cursor-default text-gray-400"
                          : "cursor-pointer hover:bg-red-100 text-gray-600 hover:text-red-600"
                      } rounded`}
                      title="Remove time"
                    >
                      <svg
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                      </svg>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
